import { getMatcher } from './getRouteMatchParser';

import { IRouter } from './IRouter';
import { IRouterState } from './IRouterState';
import { getRoute } from './getRoute';
import { IRouteChangedDetail } from './IRouteChangedDetail';
import { IParams } from './IParams';
import { toSearch } from './toSearch';
import { IRouteMatch } from './IRouteMatch';
import { wait } from '../promises';
import { IRouteMatches } from './IRouteMatches';

export function createRouter(
  routes: { [name: string]: string },
  options: {
    transitionMs?: number
  } = null
): IRouter {

  options = {
    transitionMs: 250,
    ...options
  }

  const state: IRouterState = {
    path: null,
    routes: [],
    matches: null,
    getMatch: null
  };

  const router = async () => {

    const path = {
      name: location.pathname,
      search: location.search
    };

    state.routes = Object.keys(routes)
      .map(name => getRoute({
        name,
        path: routes[name]
      }));

    if (state.path?.name != path.name
      || state.path?.search != path.search) {

      const matches: IRouteMatches = state.routes
        .reduce((m, r) => {
          if (r.match != null) m[r.id] = r.match

          return m;
        }, {});

      const eventDetail = {
        path,
        matches,
        getMatchParam: name => Object
          .values(matches)
          .find(m => m.params && m.params[name] !== undefined)
          ?.params[name]
      };

      if (!emitRouteChanging(eventDetail)) return;

      if (state.path?.name != path.name) {
        emitRouting(true);

        wait(options.transitionMs).then(() => {

          emitRouting(false);
        })
      }

      state.path = path;
      state.matches = matches;
      state.getMatch = getMatcher(matches);

      emitRouteChanged(eventDetail);
    }
  };

  router.state = state;

  router.go = (path: string | number, replace: boolean = false) => {
    const historyState = { top: `${Math.round(document.scrollingElement.scrollTop)}` };

    console.log('router.go', { path, historyState })
    history.replaceState(historyState, null, location.href);

    if (typeof path === 'number') {
      history.go(path);

      return;
    }

    if (replace) {

      history.replaceState(historyState, null, path);
    }
    else {

      history.pushState({}, null, path);
    }

    router();
  }

  router.back = () => {
    history.back();
  }

  router.search = (params: IParams) => {

    const href = state.path.name
      + (params
        ? toSearch(params)
        : state.path.search
      );

    const historyState = { top: `${Math.round(document.scrollingElement.scrollTop)}` };
    history.replaceState(historyState, null, href);

    router();
  }

  router.href = (path: string, replace: boolean = false) => {

    return {
      href: path,
      ref: (el: HTMLElement) => {
        if (!el) return;

        el.toggleAttribute('is-active',
          !!state.routes.find(r => r.pathExp && r.pathExp.test(path))?.match
        );
      },
      onClick: (e: MouseEvent) => {
        if (
          e.button !== 0
          || e.ctrlKey
          || e.metaKey
          || e.shiftKey
        ) return;

        e.preventDefault();

        router.go(path, replace);
      }
    };
  };

  router.dispose = () => {

    window.removeEventListener('popstate', popStateHandler);
  };

  const popStateHandler = (_: PopStateEvent) => {
    router();
  }

  window.addEventListener('popstate', popStateHandler);

  const emitRouting = (detail: boolean) => {

    return window.dispatchEvent(new CustomEvent<boolean>(
      'routing', { detail }
    ));
  }

  const emitRouteChanging = (detail: IRouteChangedDetail) => {

    return window.dispatchEvent(new CustomEvent<IRouteChangedDetail>(
      'routeChanging',
      {
        cancelable: true,
        detail
      }
    ));
  }

  const emitRouteChanged = (detail: IRouteChangedDetail) => {

    return window.dispatchEvent(
      new CustomEvent<IRouteChangedDetail>(
        'routeChanged', { detail }
      ));
  }

  router.renderFirst = (
    renderMap: { [name: string]: (match: IRouteMatch) => any; },
    notFound?: any
  ) => {
    if (state.matches) {

      const names = Object.keys(state.matches);
      if (!!names.length) {

        const name = names[0];
        if (!!renderMap[name])
          return renderMap[name](state.matches[name]);
      }
    }

    return typeof notFound === 'function'
      ? notFound()
      : notFound;
  }

  return router;
}
